import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useEventNotifications } from "../../hooks/useEventNotifications";
import { eventNotificationsColumns } from "../EventNotificationsColumns/EventNotificationsColumns";
import { EventNotification } from "../../api/fetchEventNotifications/types";
import { useAppDispatch } from "@/app/hooks";
import { toggleNotificationChannelAction } from "../../slices/eventNotificationsSlice";

const EventNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    notifications,
    totalCount,
    loading,
    error,
    pagination,
    handlePaginationChange,
  } = useEventNotifications();

  const handleEditNotification = useCallback(
    (id: number) => {
      navigate(`/admin/event-notifications/${id}/edit`);
    },
    [navigate],
  );

  const handleToggleChannel = useCallback(
    (
      notification: EventNotification,
      channel: "email" | "sms",
      enabled: boolean,
    ) => {
      dispatch(toggleNotificationChannelAction(notification, channel, enabled));
    },
    [dispatch],
  );

  const columns = useMemo(
    () =>
      eventNotificationsColumns({
        onEditNotification: handleEditNotification,
        onToggleChannel: handleToggleChannel,
      }),
    [handleEditNotification, handleToggleChannel],
  );

  return (
    <MainPageWrapper error={error} title="Event Notifications">
      <DataTable<EventNotification>
        columns={columns}
        data={notifications}
        error={error}
        loading={loading}
        noDataMessage="No notifications found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(row) => row.id.toString()}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default EventNotificationsPage;
